/**
 * PillarCreateDialog — manual pillar authoring (Task 1 of pillar refactor).
 *
 * Wraps SDialog from `@modules/ui-components`. Form fields mirror
 * TopicPillarCreateInput in TopicPillarService. Validates `name` non-empty;
 * hidden compatibility fields keep funnelStage=MOFU and weight=0.2 for
 * legacy generation paths without surfacing them as user controls.
 */

import React, { useState } from 'react';
import {
  Stack,
  TextField,
  Alert,
} from '@mui/material';
import { SDialog } from '@modules/ui-components';
import {
  TopicPillarService,
  type TopicPillarCreateInput,
} from '../../services/TopicPillarService';

interface Props {
  open: boolean;
  projectId: string;
  authorId?: string | null;
  onClose: () => void;
  onCreated?: (pillarId: string) => void;
}

const TESTID = 'pillar-create-dialog';
const DEFAULT_FUNNEL_STAGE = 'MOFU' as const;
const DEFAULT_PILLAR_WEIGHT = 0.2;

export const PillarCreateDialog: React.FC<Props> = ({
  open,
  projectId,
  authorId,
  onClose,
  onCreated,
}) => {
  const [name, setName] = useState('');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [stance, setStance] = useState('');
  const [knowledgeContext, setKnowledgeContext] = useState('');
  const [guardrails, setGuardrails] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setSummary('');
    setDescription('');
    setStance('');
    setKnowledgeContext('');
    setGuardrails('');
    setError(null);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleSave = async () => {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required');
      return;
    }
    const input: TopicPillarCreateInput = {
      name: trimmed,
      summary: summary.trim() || undefined,
      description: description.trim() || undefined,
      authorStance: stance.trim() || undefined,
      knowledgeContext: knowledgeContext.trim() || undefined,
      guardrails: guardrails.split('\n').map((line) => line.trim()).filter(Boolean),
      funnelStage: DEFAULT_FUNNEL_STAGE,
      weight: DEFAULT_PILLAR_WEIGHT,
      authorIds: authorId ? [authorId] : undefined,
    };
    setSaving(true);
    try {
      const service = new TopicPillarService();
      const id = await service.create(projectId, input);
      onCreated?.(id);
      reset();
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to create pillar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SDialog
      open={open}
      onClose={handleClose}
      title="Add expertise pillar"
      maxWidth="sm"
      fullWidth
      data-testid={TESTID}
      primaryAction={{
        label: 'Create pillar',
        onClick: handleSave,
        loading: saving,
        disabled: !name.trim(),
        'data-testid': `${TESTID}-create-btn`,
      }}
      secondaryAction={{
        label: 'Cancel',
        onClick: handleClose,
        disabled: saving,
      }}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <TextField
          label="Name"
          required
          autoFocus
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ 'data-testid': `${TESTID}-name-input` }}
          helperText="2-5 words. Concrete, not generic. Example: 'Founder-era debugging stories'."
          fullWidth
        />
        <TextField
          label="Summary"
          size="small"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          inputProps={{ 'data-testid': `${TESTID}-summary-input` }}
          helperText="Short summary shown in generation context."
          fullWidth
        />
        <TextField
          label="Description"
          size="small"
          multiline
          minRows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          inputProps={{ 'data-testid': `${TESTID}-description-input` }}
          helperText="One sentence describing what this pillar covers."
          fullWidth
        />
        <TextField
          label="Knowledge context"
          size="small"
          multiline
          minRows={4}
          value={knowledgeContext}
          onChange={(e) => setKnowledgeContext(e.target.value)}
          inputProps={{ 'data-testid': `${TESTID}-knowledge-context-input` }}
          helperText="Main theses, beliefs, examples, and context future posts should preserve."
          fullWidth
        />
        <TextField
          label="Author stance"
          size="small"
          multiline
          minRows={2}
          value={stance}
          onChange={(e) => setStance(e.target.value)}
          inputProps={{ 'data-testid': `${TESTID}-stance-input` }}
          helperText="Why YOU own this topic — specific experience, angle, position."
          fullWidth
        />
        <TextField
          label="Guardrails"
          size="small"
          multiline
          minRows={2}
          value={guardrails}
          onChange={(e) => setGuardrails(e.target.value)}
          inputProps={{ 'data-testid': `${TESTID}-guardrails-input` }}
          helperText="One contradiction rule per line."
          fullWidth
        />
      </Stack>
    </SDialog>
  );
};
